import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useModeStore } from '@stores/modeStore.js'
import { BOTTOM_NAV_BY_MODE } from './navConfig.js'
import { NavIcon } from './NavIcons.js'
import { useAlertasCount } from '@hooks/useAlertasCount.js'
import { cfIconSvgProps } from '@utils/iconSvg.js'

const SLOT_FIT = 5

function navItemAriaLabel(
  label: string,
  showAlertBadge: boolean | undefined,
  totalAlertas: number,
): string {
  if (showAlertBadge && totalAlertas > 0) {
    return `${label} · ${totalAlertas} alertas`
  }
  if (label === 'Ferram.') return 'Ferramentas'
  if (label === 'Gastos') return 'Financeiro'
  if (label === 'Prazos') return 'Cronograma'
  if (label === 'Decisões') return 'Memorial de decisões'
  if (label === 'Tarefas') return 'Tarefas do canteiro'
  return label
}

function IconChevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg {...cfIconSvgProps(22)}>
      {dir === 'right' ? <path d='M9 6l6 6-6 6' /> : <path d='M15 6l-6 6 6 6' />}
    </svg>
  )
}

export function BottomNav() {
  const mode = useModeStore((s) => s.mode)
  const items = BOTTOM_NAV_BY_MODE[mode]
  const totalAlertas = useAlertasCount()
  const { pathname } = useLocation()
  const overflow = items.length > SLOT_FIT
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(true)

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current
    if (!el) {
      setCanLeft(false)
      setCanRight(false)
      return
    }
    setCanLeft(el.scrollLeft > 4)
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    if (!overflow) return
    const el = scrollerRef.current
    if (!el) return
    updateEdges()
    el.addEventListener('scroll', updateEdges, { passive: true })
    const ro = new ResizeObserver(updateEdges)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateEdges)
      ro.disconnect()
    }
  }, [overflow, items, updateEdges])

  useEffect(() => {
    if (!overflow) return
    const el = scrollerRef.current
    const active = el?.querySelector<HTMLElement>('.cf3-nav__item--active')
    active?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    requestAnimationFrame(updateEdges)
  }, [pathname, overflow, updateEdges])

  function page(dir: 1 | -1) {
    const el = scrollerRef.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
  }

  const links = items.map(({ to, label, icon, end, showAlertBadge }) => (
    <NavLink
      key={to}
      to={to}
      {...(end !== undefined ? { end } : {})}
      className={({ isActive }) =>
        ['cf3-nav__item', isActive ? 'cf3-nav__item--active' : ''].filter(Boolean).join(' ')
      }
      aria-label={navItemAriaLabel(label, showAlertBadge, totalAlertas)}
    >
      <span className='cf3-nav__icon-wrap' aria-hidden>
        <NavIcon icon={icon} size={22} />
        {showAlertBadge && totalAlertas > 0 && (
          <span className='cf3-nav__badge' aria-hidden>
            {totalAlertas > 9 ? '9+' : totalAlertas}
          </span>
        )}
      </span>
      <span className='cf3-nav__label'>{label}</span>
    </NavLink>
  ))

  return (
    <nav
      className={['cf3-nav', `cf3-nav--${mode}`, overflow ? 'cf3-nav--overflow' : '']
        .filter(Boolean)
        .join(' ')}
      aria-label={`Atalhos do modo ${mode === 'guia' ? 'Guia' : 'Obra'}`}
    >
      {overflow ? (
        <div ref={scrollerRef} className='cf3-nav__scroller'>
          {links}
        </div>
      ) : (
        links
      )}
      {overflow && (
        <button
          type='button'
          className='cf3-nav__more'
          aria-label={canRight ? 'Mais atalhos da obra' : 'Atalhos anteriores'}
          onClick={() => page(canRight || !canLeft ? 1 : -1)}
        >
          <span className='cf3-nav__icon-wrap' aria-hidden>
            <IconChevron dir={canRight || !canLeft ? 'right' : 'left'} />
          </span>
          <span className='cf3-nav__label'>{canRight || !canLeft ? 'Mais' : 'Voltar'}</span>
        </button>
      )}
    </nav>
  )
}
